import {
  createCalendar,
  endOfMonth,
  endOfWeek,
  getWeeksInMonth,
  isSameDay,
  startOfMonth,
  startOfWeek,
  type CalendarDate,
} from "@internationalized/date";
import { useLocale, useTranslations } from "next-intl";
import { useRef, useState } from "react";
import {
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useDateFormatter,
} from "react-aria";
import { useCalendarState, type CalendarState } from "react-stately";

import { TIMEZONE } from "@trip/constants";
import { cn, getTripMinCalendarDate } from "@trip/utils";

import { AngleDownIcon } from "~/components/icons/angle-down";
import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { CalendarIcon } from "~/components/icons/calendar-icon";
import { LoadingSpinner } from "~/components/loading-spinner";
import { Button } from "~/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

function CalendarCell({
  state,
  date,
  range,
  locale,
}: {
  date: CalendarDate;
  locale: string;
  range: number;
  state: CalendarState;
}) {
  const ref = useRef(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);

  const start = state.value ?? date;
  const end = start.add({ days: range - 1 });

  const isInRange = date.compare(start) >= 0 && date.compare(end) <= 0;

  const isLeftRound =
    isSameDay(startOfWeek(date, locale), date) ||
    isSameDay(startOfMonth(date), date) ||
    date.compare(start) === 0;

  const isRightRound =
    isSameDay(endOfWeek(date, locale), date) ||
    isSameDay(endOfMonth(date), date) ||
    date.compare(end) === 0;

  return (
    <td {...cellProps} className="relative h-11 w-12 text-neutral-500">
      <button
        type="button"
        {...buttonProps}
        className={cn(
          "absolute inset-x-0 inset-y-0.5 flex h-10 w-12",
          isLeftRound && isRightRound
            ? "rounded-full"
            : isLeftRound
              ? "rounded-l-full"
              : isRightRound
                ? "rounded-r-full"
                : "",
          isLeftRound ? "left-1 w-11" : "",
          isRightRound ? "right-1 w-11" : "",
          isInRange ? "bg-prune-200" : "",
          isDisabled || isUnavailable ? "opacity-20" : "",
        )}
        hidden={isOutsideVisibleRange}
        ref={ref}
      >
        {isSelected ? (
          <div className="bg-prune-500 absolute inset-0 z-10 rounded-full" />
        ) : null}
        <div
          className={cn(
            "absolute inset-0 z-20 flex items-center justify-center",
            isInRange ? "text-prune-500" : "",
            isSelected ? "text-white" : "",
          )}
        >
          {formattedDate}
        </div>
      </button>
    </td>
  );
}

function CalendarGrid({
  state,
  locale,
  range,
}: {
  locale: string;
  range: number;
  state: CalendarState;
}) {
  const { gridProps, headerProps, weekDays } = useCalendarGrid({}, state);

  // Get the number of weeks in the month so we can render the proper number of rows.
  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

  return (
    <table
      {...gridProps}
      cellPadding={0}
      cellSpacing={0}
      className="border-collapse border-spacing-0 text-sm"
    >
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <th key={`${day}-${index}`}>{day}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date) =>
                date ? (
                  <CalendarCell
                    locale={locale}
                    date={date}
                    range={range}
                    key={date.toString()}
                    state={state}
                  />
                ) : (
                  <td key={null} />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Calendar({
  range,
  date,
  onChange,
  isLoading,
}: {
  date: CalendarDate;
  isLoading: boolean;
  onChange: (value: CalendarDate) => void;
  range: number;
}) {
  const locale = useLocale();
  const state = useCalendarState<CalendarDate>({
    value: date,
    locale,
    createCalendar,
    minValue: getTripMinCalendarDate(),
    onChange: (state) => {
      onChange(state);
      setOpen(false);
    },
  });
  const { calendarProps, title } = useCalendar(
    {
      onChange: () => {
        setOpen(false);
      },
    },
    state,
  );
  const dateFormatter = useDateFormatter({
    dateStyle: "medium",
    timeZone: TIMEZONE,
  });
  const [open, setOpen] = useState(false);
  const t = useTranslations("TourDetails");

  return (
    <Popover
      onOpenChange={(open) => {
        setOpen(open);
      }}
      open={open}
    >
      <PopoverTrigger asChild>
        <Button
          className="w-full justify-between"
          aria-label={t("whensYourTrip")}
        >
          <div
            className={cn(
              "flex items-center gap-2",
              isLoading ? "text-neutral-500" : "",
            )}
          >
            {isLoading ? <LoadingSpinner /> : <CalendarIcon />}
            {state.value
              ? dateFormatter.format(state.value.toDate(TIMEZONE))
              : null}
          </div>
          <AngleDownIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="shadow-card-search w-auto rounded-3xl border-none p-4"
        sideOffset={8}
      >
        <div
          {...calendarProps}
          className="flex min-w-[320px] flex-col items-stretch gap-2 *:w-full"
        >
          <div className="flex items-center">
            <Button
              onClick={() => {
                state.focusPreviousPage();
              }}
              size="circle"
              variant="ghost"
            >
              <AngleLeftIcon />
            </Button>
            <div className="grow text-center text-base">{title}</div>
            <Button
              onClick={() => {
                state.focusNextPage();
              }}
              size="circle"
              variant="ghost"
            >
              <AngleRightIcon />
            </Button>
          </div>
          <CalendarGrid range={range} locale={locale} state={state} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
